import { Dispatch, SetStateAction } from 'react';

import {
  AppLadderInstanceBucketId,
  LadderInstanceDimensions,
  Rank,
  TeamLadderInstanceDimensions,
  TeamRank,
  AppTeamLadderInstanceBucketId,
} from '@playq/services-clerk';
import { GenericFailure, OffsetLimit, UserId } from '@playq/services-shared';

import { getInitialQuery } from '/shared/Table/helpers';
import { IDataStatuses } from '/component/Support/Leaderboards/LeaderboardTypes';
import { throwGenericFailure, uuidValidationSchema } from '/helpers';
import { snackbarService } from '/common/snackbarService';
import { ITableQuery } from '/shared/Table';
import { defaultTableTotal } from '/constants';

import { ILboardEntitiesState, LadderPromotionsType } from './types';
import { defaultOffsetLimit } from './constants';

export function getDimensionId(dim: LadderInstanceDimensions | TeamLadderInstanceDimensions): string {
  return JSON.stringify(dim.serialize());
}

export function setEntitiesStateAfterQuerying<EntitiesType>(
  newEntities: EntitiesType[] | undefined,
  initialState: ILboardEntitiesState<EntitiesType>
) {
  return (prevState: ILboardEntitiesState<EntitiesType>) => {
    const { offset, limit } = prevState.offsetLimit;

    if (!newEntities) {
      return initialState;
    }

    if (prevState.total >= 0 && prevState.total >= newEntities.length) {
      return { ...prevState, entities: newEntities };
    }

    if (newEntities.length === 0) {
      const { offset: prevOffset, limit: prevLimit } = prevState.offsetLimit;
      if (prevOffset > 0) {
        const newOffset = prevOffset - prevLimit;
        const newOffsetLimit = new OffsetLimit({ offset: newOffset, limit: prevLimit });
        return { ...prevState, offsetLimit: newOffsetLimit, total: prevOffset };
      }
    }

    const allEntitiesLength = offset + newEntities.length;
    const isLastPage = newEntities.length < limit;
    const newTotal = isLastPage ? allEntitiesLength : defaultTableTotal;
    return { ...prevState, entities: newEntities, total: newTotal };
  };
}

export const handleError =
  (title: string, setDataStatuses: Dispatch<SetStateAction<IDataStatuses>>) => (err: GenericFailure | Error) => {
    setDataStatuses((prevState) => ({
      ...prevState,
      error: err.message,
    }));
    snackbarService.error(`${title}-${err.message}`);
    if (err instanceof GenericFailure) {
      throwGenericFailure(err);
    }
  };

export const getUserIdFromUUID = (identity: string | undefined) => {
  if (!identity || !uuidValidationSchema.isValidSync(identity)) {
    return;
  }
  const newUserId = new UserId();
  newUserId.user = identity.trim();
  return newUserId;
};

export const setScoresStateAfterScoreUpdate =
  (updatedRank: Rank) => (prevState: ILboardEntitiesState<Rank | TeamRank>) => {
    const updatedRankIdx = prevState.entities?.findIndex(({ rank: stateRank }) => stateRank === updatedRank.rank);
    if (updatedRankIdx === undefined || updatedRankIdx === -1 || prevState.entities === undefined) {
      return prevState;
    }
    const newRanks = [
      ...prevState.entities.slice(0, updatedRankIdx),
      updatedRank,
      ...prevState.entities.slice(updatedRankIdx + 1),
    ];
    return {
      ...prevState,
      entities: newRanks,
    };
  };

export const setScoresAfterScoreDelete =
  (removedRank: Rank, nextRank?: Rank | undefined) => (prevState: ILboardEntitiesState<Rank | TeamRank>) => {
    if (prevState.entities === undefined) {
      return prevState;
    }
    const filteredRanks = prevState.entities.filter(({ rank: stateRank }) => stateRank !== removedRank.rank);
    if (!nextRank) {
      return { ...prevState, entities: filteredRanks, total: filteredRanks.length };
    }
    const newScoresTotal = prevState.total === defaultTableTotal ? defaultTableTotal : prevState.total - 1;
    return {
      ...prevState,
      entities: filteredRanks.concat(nextRank),
      total: newScoresTotal,
    };
  };

export function onQueryChange<EntitiesType>(query: ITableQuery<string>) {
  const newOffset = query.page * query.rowsPerPage;

  return (prevState: ILboardEntitiesState<EntitiesType>) => {
    const newOffsetLimit = new OffsetLimit({
      offset: newOffset,
      limit: query.rowsPerPage,
    });

    return { ...prevState, offsetLimit: newOffsetLimit };
  };
}

export function makeOffsetLimitFromQuery<EntitiesType>(
  prevState: ILboardEntitiesState<EntitiesType>,
  query: ITableQuery<string>
) {
  const newOffset = query.page * query.rowsPerPage;

  const prevOffset = prevState.offsetLimit.offset;
  if (newOffset <= prevOffset) {
    return;
  }

  return new OffsetLimit({
    offset: newOffset,
    limit: query.rowsPerPage,
  });
}

export const getDataFromUserLadders = (userLadders?: AppLadderInstanceBucketId[] | AppTeamLadderInstanceBucketId[]) => {
  const dimensions: (LadderInstanceDimensions | TeamLadderInstanceDimensions)[] = [];
  const userInstanceBucketsMap = new Map();

  if (userLadders !== undefined) {
    userLadders.forEach((userLadder: AppLadderInstanceBucketId | AppTeamLadderInstanceBucketId) => {
      const dim = userLadder.instanceId.dimensions;
      dimensions.push(dim);
      userInstanceBucketsMap.set(getDimensionId(dim), userLadder.maybeBucketId);
    });
  }

  return { dimensions, userInstanceBucketsMap };
};

export function clearQueryWithArgsChange<EntitiesType>(prevState: ILboardEntitiesState<EntitiesType>) {
  return {
    ...prevState,
    offsetLimit: defaultOffsetLimit,
    query: getInitialQuery(),
    total: -1,
  };
}

export const getPromotionStatus = (promotions: LadderPromotionsType) => {
  if (!promotions.Sandbox && !promotions.Live) return '';
  if (!promotions.Sandbox) return '(Sandbox) Not promoted';
  if (!promotions.Live) return '(Live) Not promoted';
  return '';
};
